/**
 * Cross-firm Lawyer Profiles list (staff portal).
 *
 * Mirrors the columns of account-ui's `LawyerProfileList` but:
 *  - prepends a "Firm" column rendering <FirmChip companyId={…} />
 *  - drives filters (companyId / status / q / profession) from URL query
 *    params via the shared <ProfilesListFilters> toolbar
 *  - is allowed to view + delete records from any firm (staff-bff omits
 *    X-Actor-Company-Id, so list endpoints return all firms by default).
 *
 * Visual conventions match the Companies / Subscriptions list pages — see
 * `ProfilesList.module.css`.
 *
 * See specs/profiles/staff-ui-parity.md (Group 1 — Lawyer profiles).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button, Select, type Column } from "@legal-500/legal500-design-system";
import { PageHeader } from "@/components/PageHeader";
import { DataTable } from "@/components/DataTable/DataTable";
import { ConfirmationModal } from "@/components/Modal/ConfirmationModal";
import { PaginationControls } from "@/components/PaginationControls";
import {
  FirmChip,
  ProfilesFilterCell,
  ProfilesListFilters,
  RegionNodeChip,
} from "@/components/Profiles/ListFilters";
import { lawyerProfilesService } from "@/api/profiles";
import type { ContentStatus, LawyerProfile, Profession } from "@/api/profiles/types";
import { parseRegionKind } from "@/hooks/useRegions";
import { ROUTES } from "@/routes";
import styles from "../ProfilesList.module.css";
import { toEditionRows } from "./toEditionRows";

const PAGE_SIZE = 20;

const PROFESSION_OPTIONS = [
  { value: "", label: "All professions" },
  { value: "lawyer", label: "Lawyer" },
  { value: "barrister", label: "Barrister" },
  { value: "clerk", label: "Clerk" },
  { value: "team_member", label: "Team member" },
];

function parseProfession(raw: string | null): Profession | undefined {
  if (raw === "lawyer" || raw === "barrister" || raw === "clerk" || raw === "team_member")
    return raw;
  return undefined;
}

function formatDate(iso: string | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString();
}

const WEBSITE_LANGUAGE_LABELS: Record<string, string> = {
  com_en: "English",
  com_zh: "中文",
  fr: "French",
  de: "German",
};

function languageLabelOf(row: LawyerProfile): string {
  const website = row.website;
  if (website && WEBSITE_LANGUAGE_LABELS[website]) return WEBSITE_LANGUAGE_LABELS[website];
  return row.languageCode ?? website ?? "—";
}

export function LawyerProfilesList() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const companyId = searchParams.get("companyId") || undefined;
  const status = searchParams.get("status") || undefined;
  const q = searchParams.get("q") || undefined;
  const regionKind = parseRegionKind(searchParams.get("regionKind"));
  const regionId = searchParams.get("regionId") || undefined;
  const profession = parseProfession(searchParams.get("profession"));
  const offset = Number(searchParams.get("offset") ?? "0") || 0;

  const [rows, setRows] = useState<LawyerProfile[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<LawyerProfile | null>(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await lawyerProfilesService.list({
        companyId,
        status,
        q,
        regionKind,
        regionId,
        profession,
        limit: PAGE_SIZE,
        offset,
      });
      setRows(toEditionRows(res.data, status as ContentStatus | undefined));
      setTotal(res.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load lawyer profiles");
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [companyId, status, q, regionKind, regionId, profession, offset]);

  useEffect(() => {
    void fetchList();
  }, [fetchList]);

  const handleProfessionChange = (value: string) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (value) next.set("profession", value);
      else next.delete("profession");
      // Reset paging when filter changes.
      next.delete("offset");
      return next;
    });
  };

  const handlePage = (page: number) => {
    const nextOffset = Math.max(0, (page - 1) * PAGE_SIZE);
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (nextOffset > 0) next.set("offset", String(nextOffset));
      else next.delete("offset");
      return next;
    });
  };

  const companyIds = useMemo(() => Array.from(new Set(rows.map(r => String(r.companyId)))), [rows]);
  // companyIds is consumed indirectly through <FirmChip> which batches via
  // useCompanyNames. We surface the list here for any future "select all
  // names up-front" optimisation and to document the batching contract.
  void companyIds;

  const handleDelete = async () => {
    if (!deleteTarget) return;
    await lawyerProfilesService.remove(deleteTarget.id);
    setDeleteTarget(null);
    await fetchList();
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;

  const fullNameOf = (row: LawyerProfile) =>
    `${row.firstName} ${row.lastName}`.trim() || `Profile ${row.id.slice(0, 8)}`;

  // Columns for the shared <DataTable>. Columns that don't map to a single
  // field (Regions, Actions) use a valid `keyof LawyerProfile` accessor purely
  // to satisfy the type and supply a `render`. The Regions column opts into
  // `wrap` so the region chips can flow onto multiple lines.
  const columns: Column<LawyerProfile>[] = [
    {
      header: "Firm",
      accessor: "companyId",
      render: (_value, row) => <FirmChip companyId={row.companyId} />,
    },
    {
      header: "Name",
      accessor: "lastName",
      render: (_value, row) => (
        <span data-testid={`lawyer-profile-row-${row.id}`}>
          <Link to={ROUTES.profiles.lawyerProfileDetail(row.id)} className={styles.titleLink}>
            {fullNameOf(row)}
          </Link>
        </span>
      ),
    },
    {
      header: "Language",
      accessor: "website",
      render: (_value, row) => (
        <span data-testid={`lawyer-profile-language-${row.id}`}>{languageLabelOf(row)}</span>
      ),
    },
    {
      header: "Profession",
      accessor: "profession",
      render: (_value, row) => row.profession ?? "—",
    },
    {
      header: "Regions",
      accessor: "regions",
      wrap: true,
      render: (_value, row) => (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
          {(row.regions ?? []).map(r => (
            <RegionNodeChip key={`${r.kind}:${r.id}`} region={r} />
          ))}
        </div>
      ),
    },
    {
      header: "Email",
      accessor: "email",
      render: (_value, row) => row.email ?? "—",
    },
    {
      header: "Status",
      accessor: "status",
      render: (_value, row) => (
        <span className={styles.statusPill} data-status={row.status}>
          {row.status}
        </span>
      ),
    },
    {
      header: "Updated",
      accessor: "updatedAt",
      render: (_value, row) => <span className={styles.muted}>{formatDate(row.updatedAt)}</span>,
    },
    {
      header: "",
      accessor: "id",
      render: (_value, row) => {
        const fullName = fullNameOf(row);
        return (
          <div className={styles.actions}>
            <Button
              variant="outlined"
              size="small"
              aria-label={`View ${fullName}`}
              onClick={() => navigate(ROUTES.profiles.lawyerProfileDetail(row.id))}
            >
              View
            </Button>
            <Button
              variant="outlined"
              size="small"
              aria-label={`Edit ${fullName}`}
              onClick={() => navigate(ROUTES.profiles.lawyerProfileDetail(row.id))}
            >
              Edit
            </Button>
            <Button
              variant="text"
              color="error"
              size="small"
              aria-label={`Delete ${fullName}`}
              onClick={() => setDeleteTarget(row)}
            >
              Delete
            </Button>
          </div>
        );
      },
    },
  ];

  return (
    <div className={styles.wrapper}>
      <PageHeader title="Lawyer profiles">
        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate(ROUTES.profiles.lawyerProfileNew)}
        >
          New lawyer profile
        </Button>
      </PageHeader>

      <ProfilesListFilters enabledFilters={{ company: true, status: true, q: true, region: true }}>
        <ProfilesFilterCell>
          <Select
            label="Profession"
            value={profession ?? ""}
            options={PROFESSION_OPTIONS}
            onChange={e => handleProfessionChange(e.target.value)}
            size="small"
          />
        </ProfilesFilterCell>
      </ProfilesListFilters>

      <div className={styles.contentSection}>
        {error && (
          <div role="alert" className={styles.errorBanner}>
            {error}
          </div>
        )}

        {loading ? (
          <p role="status" className={styles.muted}>
            Loading lawyer profiles…
          </p>
        ) : rows.length === 0 ? (
          <p data-testid="lawyer-profiles-empty" className={styles.emptyState}>
            No lawyer profiles match the current filters.
          </p>
        ) : (
          <>
            <DataTable data={rows} columns={columns} rowKey="id" />

            <PaginationControls
              currentPage={currentPage}
              totalPages={totalPages}
              startIndex={offset}
              endIndex={offset + rows.length}
              totalCount={total}
              itemName="lawyer profiles"
              onPageChange={handlePage}
            />
          </>
        )}
      </div>

      {deleteTarget && (
        <ConfirmationModal
          title={`Delete lawyer profile "${deleteTarget.firstName} ${deleteTarget.lastName}"?`}
          message="This will permanently delete the lawyer profile."
          warningMessage="This action cannot be undone."
          confirmButtonText="Delete"
          confirmButtonColor="error"
          onClose={() => setDeleteTarget(null)}
          onConfirm={handleDelete}
        />
      )}
    </div>
  );
}

export default LawyerProfilesList;
